import { Trans } from "@lingui/macro";
import { FC, useCallback, useState } from "react";
import Link from "next/link";
import { useIsLoggedIn } from "src/hooks/useIsLoggedIn";
import { signOut } from "src/utils/nextAuth";
import { Button } from "src/atoms/Button";
import { StyledUserMenu } from "./UserMenu.styled";
import { User } from "src/icons";
import {
  FlipBackwardIcon,
  HelpCircleIcon,
  LogOutIcon,
  ShoppingBagIcon,
} from "@otrium/icons";
import { Box } from "@otrium/core";
import { getI18nRoute } from "src/utils/i18nRoutes";
import getConfig from "next/config";
import { Locale } from "lib/Intl";

interface Props {
  onToggle?: () => void;
}

const { publicRuntimeConfig } = getConfig();
const locale = publicRuntimeConfig.LOCALE as Locale;

const UserMenu: FC<Props> = ({ onToggle }) => {
  const isLoggedIn = useIsLoggedIn();
  const [showSignOutButtonUntilRedirect, setShowSignOutButtonUntilRedirect] =
    useState<boolean | undefined>(undefined);

  const handleLogout = useCallback(() => {
    setShowSignOutButtonUntilRedirect(true);
    void signOut();
  }, []);

  return (
    <StyledUserMenu>
      <li>
        <Link
          href={isLoggedIn ? "/my-orders" : "/sign-in?redirect_to=/my-orders"}
          className="UserMenu__link"
          prefetch={false}
          onClick={() => (onToggle ? onToggle() : undefined)}
        >
          <ShoppingBagIcon />
          <Trans>My Orders</Trans>
        </Link>
      </li>
      <li>
        <Link
          href={isLoggedIn ? "/returns" : "/sign-in?redirect_to=/returns"}
          className="UserMenu__link"
          prefetch={false}
          onClick={() => (onToggle ? onToggle() : undefined)}
        >
          <FlipBackwardIcon />
          <Trans>Create a Return</Trans>
        </Link>
      </li>
      <li>
        <Link
          href={isLoggedIn ? "/my-profile" : "/sign-in?redirect_to=/my-profile"}
          prefetch={false}
          className="UserMenu__link"
          data-testid="user-menu-my-profile"
          onClick={() => (onToggle ? onToggle() : undefined)}
        >
          <User />
          <Trans>My Profile</Trans>
        </Link>
      </li>
      <li>
        <Link
          href={getI18nRoute({
            route: "/faq",
            locale,
          })}
          className="UserMenu__link"
          target="_blank"
        >
          <HelpCircleIcon />
          <Trans>Customer Service</Trans>
        </Link>
      </li>
      <li>
        {isLoggedIn || showSignOutButtonUntilRedirect ? (
          <Box
            role="button"
            data-testid="user-menu-logout-button"
            disabled={showSignOutButtonUntilRedirect}
            onClick={handleLogout}
            className="logout"
          >
            <LogOutIcon />
            <Trans>Logout</Trans>
          </Box>
        ) : (
          <div className="UserMenu__actions">
            <Button as="a" href={"/sign-in"} variant="secondary" fullWidth>
              <Trans>Login</Trans>
            </Button>
            <Button as="a" href={"/register"} variant="primary" fullWidth>
              <Trans>Create an account</Trans>
            </Button>
          </div>
        )}
      </li>
    </StyledUserMenu>
  );
};

export { UserMenu };
